import React, { PropsWithChildren } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';

import './PressRelease.css';
import { getSecChildren } from '../PageSection/PageSection';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { ButtonWrapper } from '../LinkWrapper/LinkWrapper';
import { Title } from '../../importCL';
import withAsyncImport from '../../withAsyncImport';
const CobGrid = withAsyncImport(() => import('../CobGrid/CobGrid'));

type ITitleSizes = 'h1' | 'h2' | 'h3' | 'h4' | 'h5';

interface IPressReleaseProps extends MappedComponentProperties {
    title?: string;
    subtitle?: string;
    location?: string;
    publishDate?: Date;
    titleTagSize?: ITitleSizes;
    headerTagSize?: ITitleSizes;
    subTitleTagSize?: ITitleSizes;
    thumbnailUrl?: string;
    thumbnailAltText?: string;
    backButtonOverrideUrl?: string;
    disclaimer?: string;
    cqItems?: any;
    cqItemsOrder?: string[];
}

export const PressReleaseEditConfig = {
    emptyLabel: 'Press Release',
    isEmpty: (props: PropsWithChildren<IPressReleaseProps>) => {
        return !props || !(props.title || props.subtitle);
    },
};

const PressRelease = (props: PropsWithChildren<IPressReleaseProps>): JSX.Element => {
    interface DateLocationParams {
        date?: Date;
        location?: string;
    }

    const getFormattedDate = React.useCallback((date: Date) => {
        return new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }, []);

    const getDateLocation = React.useCallback(({ date, location }: DateLocationParams) => {
        if (date && location) {
            return props.location + ' · ' + getFormattedDate(date);
        }

        if (date) {
            return getFormattedDate(date);
        }

        return location;
    }, []);

    return (
        <div id='press-release' className='PressRelease'>
            <div className='PressRelease__header'>
                {props.title && (
                    <Title tag={props.titleTagSize || 'h2'} className='PressRelease__title'>{transformHTMLParserLinks(props.title)}</Title>
                )}

                {(props.location || props.publishDate) && (
                    <Title tag={props.headerTagSize || 'h5'} className='PressRelease__subtitle--header'>
                        {getDateLocation({ date: props.publishDate, location: props.location })}
                    </Title>
                )}
            </div>

            {props.subtitle && (
                <div className='PressRelease__subheader'>
                    <hr className='PressRelease__separator' />

                    <Title tag={props.subTitleTagSize || 'h3'} className='PressRelease__subtitle--subheader'>
                        {transformHTMLParserLinks(props.subtitle)}
                    </Title>
                </div>
            )}

            {props.cqItems && props.cqItems.release &&
                /* @ts-ignore */
                <CobGrid isInEditor={props.isInEditor} cqPath={`${props.cqPath}/release`} cqItems={getSecChildren(props, 'release')} cqItemsOrder={props.cqItems.release[':itemsOrder']} />
            }

            <div className='PressRelease__buttonContainer'>
                <ButtonWrapper className='PressRelease__backButton'
                    href={props.backButtonOverrideUrl}
                    icon='back'
                    size='medium'
                    variant='link'>
                    Back to Press Releases
                </ButtonWrapper>
            </div>

            <hr className='PressRelease__divider' />

            {transformHTMLParserLinks(props.disclaimer || '')}
        </div>
    );
};

export default PressRelease;
